/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The phone's standing bar on a client: reach them, or book something.
 *
 * Two things a broker does with a client while away from a desk, given
 * equal width because neither is the obvious one: book a visit, or read
 * what the client has been writing. WhatsApp keeps a 48px square beside
 * them — its badge is the one icon nobody has to be taught.
 *
 * "Redes sociais" is spelled out rather than drawn as a speech bubble. A
 * bubble was about to mean two different things on this screen: these are
 * the client's own messages from Instagram and Messenger, and the assistant
 * is a separate conversation with a separate purpose. The label is what
 * keeps them apart, so it is not something to shorten later.
 *
 * The number no longer has a copy button. It is in the header band above,
 * as text, one tap away from a long-press — a dedicated 48px target for
 * putting it on the clipboard was buying very little of this row.
 *
 * A flex sibling of the scrolling column, never `position: fixed`: the shell
 * pins `html` and `body` at `overflow: hidden`, so a fixed bar would anchor
 * to a viewport this content does not scroll within.
 */

import { useTranslation } from 'react-i18next';
import {Button, Anchor} from '@/shared/ui';
import { WhatsAppIcon } from '@/shared/ui/components/WhatsAppQRCode.parts';
import { SocialUnseenDot } from '../SocialUnseenDot';

interface ClientMobileActionBarProps {
  /** Opens the conversation; omitted when the client has no WhatsApp. */
  whatsAppHref?: string;
  onScheduleVisit: () => void;
  /** Raises the social panel over this screen. */
  onOpenSocial: () => void;
  /** Marks the button when the client has written and nobody has looked. */
  hasUnseenSocial?: boolean;
}

export const ClientMobileActionBar: React.FC<ClientMobileActionBarProps> = ({
  whatsAppHref, onScheduleVisit, onOpenSocial, hasUnseenSocial,
}) => {
  const { t } = useTranslation('features/clients');

  return (
    <div
      data-testid="client-mobile-action-bar"
      className="z-[30] flex flex-none gap-(--space-2) border-t border-[var(--border-subtle)] bg-[var(--surface-primary)] px-(--space-4) py-(--space-3)"
    >
      {whatsAppHref && (
        <Anchor
          href={whatsAppHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="client-mobile-whatsapp"
          aria-label={t('detail.channels.whatsapp')}
          className="flex h-[48px] w-[48px] flex-none items-center justify-center rounded-(--radius-md) no-underline!"
          style={{
            border: '1px solid #25D366',
            background: 'color-mix(in srgb, #25D366 10%, transparent)',
            color: '#128C7E',
          }}
        >
          <WhatsAppIcon className="h-6 w-6" />
        </Anchor>
      )}

      <Button
        variant="solid"
        onClick={onScheduleVisit}
        data-testid="client-mobile-schedule"
        className="h-[48px] min-w-0 flex-1 px-(--space-2) text-[length:var(--text-body-sm)] [font-weight:var(--font-bold)]"
      >
        {t('detail.actions.scheduleVisit')}
      </Button>

      <Button
        variant="outlined"
        onClick={onOpenSocial}
        data-testid="client-mobile-social"
        className="relative h-[48px] min-w-0 flex-1 px-(--space-2) text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)]"
      >
        {t('detail.actions.socialChat')}
        <SocialUnseenDot show={Boolean(hasUnseenSocial)} />
      </Button>
    </div>
  );
};

export default ClientMobileActionBar;
